import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import MaterialSelect from "@mui/material/Select";
import { Box, MenuItem } from "@mui/material";
import moment from "moment";
import VcDatePicker from "Components/UI/DatePicker/DatePicker";
import "../RegisterEquipment/style.css";
import "../../../../Assets/css/date-picker.css";
import images from "Assets/images";
import Label from "Components/UI/Label";
import Input from "Components/UI/Input";
import { scrollBar } from "Components/Common/Header/ManageOutsideClickClose";
import { getLocalStorage } from "Network/ApiService";
import { ls_vars } from "Library/Constants";
import { useApiCall } from "Hooks/useApiCall";
import { getEquipmentTypes } from "Network/Core/EquipmentMaster/equipment";
import {
  getActivities,
  getFilteredCommonUtils,
} from "Network/Core/CommonUtils/commonUtils";
import { createEquipmentRegistry } from "Network/Core/EquipmentMaster/equipmentRegistry";
import { EquipmentRegistryRequest } from "Network/Core/EquipmentMaster/types";
import { da, fi } from "date-fns/locale";
import { useNav } from "Hooks/useNav";
import toast from "react-hot-toast";
import Button from "Components/UI/Button";
import EquipmentMasterModal from "Components/Common/Modal/EquipmentMasterModal";
import {
  EquipmentType,
  GetEquipmentListParams,
} from "Network/Core/EquipmentMaster/types";
import { useTranslationCheck } from "Hooks/useTranslationCheck";

type FormValues = {
  arrivalDate: Date | null;
  equipmentType: string;
  companyName: string;
  equipmentMake: string;
  equipmentModel: string;
  equipmentSerialNumber: string;
  responsiblePerson: string;
  contactNumber: string;
  emailAddress: string;
  address: string;
  useMyAddress: string;
  recepientName: string;
  modelName: string;
  yearOfManufactoring: string;
  fuelType: string;
  typeOfUsage: string | number;
  additionalNotes: string;
};

const RegisterEquipment: React.FC = () => {
  const { t, tCheck } = useTranslationCheck();
  const { call } = useApiCall();
  const { viewListEquipments } = useNav();
  const project = getLocalStorage(ls_vars.PROJECT_DATA);
  const region = getLocalStorage(ls_vars.REGION_ID);
  const site = getLocalStorage(ls_vars.PROJECT_SITE_ID);
  const [currentStep, setCurrentStep] = useState(0); // Initial step is 0 (empty stepper)
  const user = getLocalStorage(ls_vars.USER_DATA);
  const [isScrolling, setIsScrolling] = useState<boolean>(false);
  const [useMyAddress, setUseMyAddress] = useState<boolean>(false);
  const [equipmentTypesOptions, setEquipmentTypesOptions] = useState<any[]>([]);
  const [yearOfManufactoringOptions, setYearOfManufactoringOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [fuelTypeOptions, setFuelTypeOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [typeOfUsageOptions, setTypeOfUsageOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [isEquipmentModalOpen, setIsEquipmentModalOpen] =
    useState<boolean>(false);
  const [categoryOptions, setCategoryOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [existingData, setExistingData] = useState<EquipmentType>(
    {} as EquipmentType
  );
  const {
    control,
    setValue,
    register, // this use for events and value
    handleSubmit, // this is form submit handle
    setError,
    clearErrors,
    trigger, // Add this
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      arrivalDate: new Date(),
      equipmentType: "",
      companyName: "",
      equipmentMake: "",
      equipmentModel: "",
      equipmentSerialNumber: "",
      responsiblePerson: "",
      contactNumber: "",
      emailAddress: "",
      address: "",
      useMyAddress: "",
      recepientName: "",
      modelName: "",
      yearOfManufactoring: "",
      fuelType: "",
      typeOfUsage: "",
      additionalNotes: "",
    },
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  scrollBar(setIsScrolling);

  const handleNext = async () => {
    if (currentStep === 0) {
      // Validate first step fields
      const isValid = await trigger(["arrivalDate", "equipmentType"]);
      if (isValid) {
        setCurrentStep((prev) => Math.min(prev + 1, 2));
      }
    } else if (currentStep === 1) {
      // Validate second step fields
      const isValid = await trigger([
        "companyName",
        "recepientName",
        "emailAddress",
        "contactNumber",
      ]);
      if (isValid) {
        setCurrentStep((prev) => Math.min(prev + 1, 2));
      }
    }
  };

  const getCategories = async () => {
    call(
      () => getFilteredCommonUtils("EQUIPMENT_CATEGORY", region),
      (data) => {
        const options = data?.map((item: any) => ({
          label: item.name,
          value: item.id,
        }));
        setCategoryOptions(options);
      }
    );
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0)); // Step only decrements to a min of 0
  };

  const fetchEquipmentTypes = async () => {
    call(
      () => getEquipmentTypes(region, project.ref, site),
      (res: any) => {
        const options = res?.map((item: any) => ({
          label: `${item.brand}-${item.name}-${item.model}-${item.fuelTypeName}`,
          value: item.id,
          brand: item.brand,
          name: item.name,
          model: item.model,
          fuelType: item.fuelType,
          fuelTypeName: item.fuelTypeName,
        }));
        const sortedOptions = options?.sort((a: any, b: any) =>
          a.label.localeCompare(b.label)
        );
        setEquipmentTypesOptions(sortedOptions);
      }
    );
  };

  const buildYearOfManufactor = () => {
    const currentYear = new Date().getFullYear();
    const years = [];

    for (let year = currentYear; year >= 1900; year--) {
      years.push({ label: year.toString(), value: year.toString() });
    }

    setYearOfManufactoringOptions(years);
  };

  const getUsageTypes = async () => {
    call(
      () => getFilteredCommonUtils("EQUIPMENT_USAGE_TYPE", region),
      (data) => {
        const options = data?.map((item: any) => ({
          label: item.name,
          value: item.id,
        }));
        const sortedOptions = options?.sort((a: any, b: any) =>
          a.label.localeCompare(b.label)
        );
        setTypeOfUsageOptions(sortedOptions);
      }
    );
  };

  const getFuelTypes = async () => {
    call(
      () => getFilteredCommonUtils("FUEL_TYPE", region),
      (data) => {
        const options = data?.map((item: any) => ({
          label: item.name,
          value: item.id,
        }));
        setFuelTypeOptions(options);
      }
    );
  };

  const handleEquipmentTypeChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    let selectedValue = e.target.value;
    const selectedEquipment = equipmentTypesOptions.find(
      (item) => item.value === selectedValue
    );

    if (selectedEquipment) {
      setValue("equipmentType", selectedValue);
      setValue("equipmentMake", selectedEquipment.brand);
      setValue("equipmentModel", selectedEquipment.model);
      setValue("fuelType", selectedEquipment.fuelType.toString());
    } else {
      // Clear the fields if no equipment is selected
      setValue("equipmentMake", "");
      setValue("equipmentModel", "");
      setValue("fuelType", "");
    }
  };

  const handleChangeCheckBox = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!useMyAddress) {
      setValue("companyName", user.organization.name);
      setValue("recepientName", user.name);
      setValue("address", user.organization.address1);
      setValue("contactNumber", user.phoneNumber);
      setValue("emailAddress", user.email);
    } else {
      setValue("companyName", "");
      setValue("recepientName", "");
      setValue("address", "");
      setValue("contactNumber", "");
      setValue("emailAddress", "");
    }
    setUseMyAddress((prev) => !prev);
  };

  const handleAddEquipmentType = () => {
    setIsEquipmentModalOpen(true);
    getCategories();
  };

  const onSubmit = async (data: FormValues) => {
    // Only proceed if we're on the final step
    if (currentStep === 2) {
      // Validate only required fields
      const isValid = await trigger([
        "equipmentType",
        "companyName",
        "recepientName",
        "emailAddress",
        "contactNumber",
        "equipmentMake",
        "equipmentModel",
        "yearOfManufactoring",
        "fuelType",
        "typeOfUsage",
      ]);

      if (!isValid) {
        toast.error(t("Translation.Err_missing_some_required_fields"));
        return;
      }

      const request: EquipmentRegistryRequest = {
        scheduled_arrival: moment(data.arrivalDate).format("YYYY-MM-DD"),
        equipment_type_id: parseInt(data.equipmentType),
        company: data.companyName,
        contact_name: data.recepientName,
        email_address: data.emailAddress,
        contact_number: data.contactNumber,
        reg_make: data.equipmentMake,
        reg_model: data.equipmentModel,
        reg_year: parseInt(data.yearOfManufactoring),
        address: data.address,
        fuel_type: parseInt(data.fuelType),
        type_of_usage: data.typeOfUsage,
        additional_notes: data.additionalNotes,
      };
      handleCreateEquipmentRegistry(request);
    }
  };

  const handleCreateEquipmentRegistry = async (data: any) => {
    call(
      () => createEquipmentRegistry(data, region, project.ref, site),
      undefined,
      {
        successMessage: "Translation.equipment_registry_add_success",
        onError: (error) => {
          if (error?.error) toast.error(t(`Translation.${error.error}`));
          else if (error) toast.error(t(`Translation.${error}`));
        },
      }
    );
    viewListEquipments(project);
  };

  useEffect(() => {
    const fetchData = async () => {
      await getFuelTypes();
      await getUsageTypes();
      await fetchEquipmentTypes();
      await buildYearOfManufactor();
    };
    fetchData();
  }, []);

  const FieldError: React.FC<{ error?: string }> = ({ error }) => {
    return error ? (
      <div
        className="error-message"
        style={{ color: "red", fontSize: "12px", marginTop: "4px" }}
      >
        {error}
      </div>
    ) : null;
  };

  const handleEquipmentModalClose = () => {
    setIsEquipmentModalOpen(false);
    fetchEquipmentTypes();
  };
  return (
    <>
      {isEquipmentModalOpen && (
        <>
          <EquipmentMasterModal
            handleClose={() => handleEquipmentModalClose()}
            existingData={existingData}
            categoryOptions={categoryOptions}
            fuelTypeOptions={fuelTypeOptions}
          />
          {/* <div className="modal-backdrop fade show" /> */}
        </>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="page-wrapper">
        <div className="page-header">
          <h3 className="page-title">{t("Translation.register_equipment")}</h3>
        </div>
        <div className="page-content custom-list-div w-100" style={{overflowY: "auto" }}>
          {currentStep === 0 && (
            <div className="steps-list">
              <div className="steps active">
                <div className="line"></div>
                <div className="step-title">
                  <span className="circle"></span>
                  <span className="s-title">
                    {t("Translation.register_equipment")}
                  </span>
                </div>
              </div>

              <div className="steps">
                <div className="line"></div>
                <div className="step-title">
                  <span className="circle"></span>
                  <span className="s-title">
                    {t("Translation.equipment_registration")}
                  </span>
                </div>
              </div>
            </div>
          )}

          {currentStep === 1 && (
            <div className="steps-list">
              <div className="steps completed">
                <div className="line"></div>
                <div className="step-title">
                  <img src={images.completedStep}></img>
                  <span className="s-title">
                    {t("Translation.register_equipment")}
                  </span>
                </div>
              </div>
              <div className="steps active">
                <div className="line"></div>
                <div className="step-title">
                  <span className="circle"></span>
                  <span className="s-title">
                    {t("Translation.equipment_registration")}
                  </span>
                </div>
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="steps-list">
              <div className="steps completed">
                <div className="line"></div>
                <div className="step-title">
                  <img src={images.completedStep}></img>
                  <span className="s-title">
                    {t("Translation.register_equipment")}
                  </span>
                </div>
              </div>
              <div className="steps active">
                <div className="line"></div>
                <div className="step-title">
                  <span className="circle"></span>
                  <span className="s-title">
                    {t("Translation.equipment_registration")}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Form Content */}

          {currentStep === 0 && (
            <div className="vehicle-form " style={{ margin: "30px" }}>
              <div className="col-xl-4 col-lg-6 col-md-12">
                <div className="form-group ">
                  <Label label={t("Translation.date_of_equipment_arrival")} />

                  <Box className="date-box-frontend ">
                    <Controller
                      name="arrivalDate"
                      control={control}
                      rules={{
                        required: {
                          value: true,
                          message: t("Translation.msg_arrival_date_is_required"),
                        },
                      }}
                      render={({ field }) => (
                        <VcDatePicker
                          {...field}
                          date={field.value || null}
                          format="DD/MM/YYYY"
                          onDateChange={(val: Date | null) =>
                            field.onChange(val)
                          }
                        />
                      )}
                    />
                  </Box>
                </div>

                <div className="form-group">
                  <Label label={t("Translation.equipment_type")} />
                  <span className="text-danger fs-1">*</span>
                  <Controller
                    name="equipmentType"
                    control={control}
                    rules={{
                      required: {
                        value: true,
                        message: t("Translation.msg_equipment_type_is_required"),
                      },
                    }}
                    render={({ field }) => (
                      <>
                        <Box display="flex" alignItems="center" gap={2}>
                          <MaterialSelect
                            {...field}
                            onChange={(e: any) => {
                              field.onChange(e);
                              handleEquipmentTypeChange(e);
                            }}
                            error={!!errors.equipmentType}
                            displayEmpty
                            id="equipmentType"
                            sx={{
                              width: "100%",
                              height: "48px",
                              background: "#EFF3F6",
                              color: "#A0AEBD",
                              border: "none",
                              "& .MuiOutlinedInput-notchedOutline": {
                                border: "none",
                              },
                            }}
                          >
                            {(equipmentTypesOptions || []).map((item) => (
                              <MenuItem value={item.value} key={item.value}>
                                {tCheck(item.label)}
                              </MenuItem>
                            ))}
                          </MaterialSelect>
                          <Button
                            handleClick={handleAddEquipmentType}
                            className="btn theme-btn"
                            buttonLabel={
                              <span>
                                <img src={images.plusIcon} alt="plus" />
                              </span>
                            }
                          />
                        </Box>
                        <FieldError error={errors.equipmentType?.message} />
                      </>
                    )}
                  />
                </div>
              </div>
            </div>
          )}

          {currentStep === 1 && (
            <div className="vehicle-form" style={{ margin: "30px" }}>
              <div className="row">
                <div className="col-xl-6 col-lg-8 col-md-12">
                  <div>
                    <h3>
                      {" "}
                      {t(
                        "Translation.details_of_individual_responsible_for_equipment"
                      )}
                    </h3>
                    <div className="form-group">
                      <div className="filtercheck-item">
                        <input
                          type="checkbox"
                          name={"checkbox"}
                          onChange={(e) => handleChangeCheckBox(e)}
                          checked={useMyAddress}
                          //   disabled={!isEditable}
                          id="useMyAddress"
                        />
                        <span className="fci-title">
                          {t(`Translation.use_my_details`)}
                        </span>
                      </div>
                    </div>

                    <div className="form-group">
                      <Label label={t("Translation.company_name")} />
                      <span className="text-danger fs-1">*</span>
                      <Controller
                        name="companyName"
                        control={control}
                        rules={{ required: t("Translation.msg_company_name_is_required") }}
                        render={({ field }) => (
                          <>
                            <Input
                              {...field}
                              inputType="text"
                              className={"form-control w-full"}
                              inputName="companyName"
                              register={register}
                              rules={{
                                required: {
                                  value: true,
                                  message: `${t(
                                    "Translation.error_siteadd_required"
                                  )}`,
                                },
                                //   onChange: (e: any) => onChangeInput(e),
                                //   onBlur: () => onBlurAddress(),
                              }}
                              keyPressHandler={(e: any) => {}}
                              id="companyName"
                              disabled={false}
                            />
                            <FieldError error={errors.companyName?.message} />
                          </>
                        )}
                      />
                    </div>
                    <div className="form-group">
                      <Label label={t("Translation.recepient_name")} />
                      <span className="text-danger fs-1">*</span>
                      <Controller
                        name="recepientName"
                        control={control}
                        render={({ field }) => (
                          <>
                            <Input
                              inputType="text"
                              className={"form-control"}
                              inputName="recepientName"
                              register={register}
                              rules={{
                                required: {
                                  value: true,
                                  message: `${t(
                                    "Translation.error_siteadd_required"
                                  )}`,
                                },
                                //   onChange: (e: any) => onChangeInput(e),
                                //   onBlur: () => onBlurAddress(),
                              }}
                              keyPressHandler={(e: any) => {}}
                              id="recepientName"
                              disabled={false}
                            />
                            <FieldError error={errors.recepientName?.message} />
                          </>
                        )}
                      />
                    </div>
                    <div className="form-group">
                      <Label label={t("Translation.email_address")} />
                      <span className="text-danger fs-1">*</span>
                      <Controller
                        name="emailAddress"
                        control={control}
                        rules={{
                          required: "Email is required",
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: t("Translation.msg_invalid_email_address"),
                          },
                        }}
                        render={({ field }) => (
                          <>
                            <Input
                              inputType="text"
                              className={"form-control"}
                              inputName="emailAddress"
                              register={register}
                              rules={{
                                required: {
                                  value: true,
                                  message: `${t(
                                    "Translation.error_siteadd_required"
                                  )}`,
                                },
                                //   onChange: (e: any) => onChangeInput(e),
                                //   onBlur: () => onBlurAddress(),
                              }}
                              keyPressHandler={(e: any) => {}}
                              id="emailAddress"
                              disabled={false}
                              {...field}
                            />
                            <FieldError error={errors.emailAddress?.message} />
                          </>
                        )}
                      />
                    </div>
                    <div className="form-group">
                      <Label label={t("Translation.mobile_number")} />
                      <span className="text-danger fs-1">*</span>
                      <Controller
                        name="contactNumber"
                        control={control}
                        render={({ field }) => (
                          <Input
                            inputType="text"
                            className={"form-control"}
                            inputName="contactNumber"
                            register={register}
                            rules={{
                              required: {
                                value: true,
                                message: `${t(
                                  "Translation.error_siteadd_required"
                                )}`,
                              },
                              //   onChange: (e: any) => onChangeInput(e),
                              //   onBlur: () => onBlurAddress(),
                            }}
                            keyPressHandler={(e: any) => {}}
                            id="contactNumber"
                            disabled={false}
                          />
                        )}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {currentStep === 2 && (
            <div className="vehicle-form" style={{ margin: "30px" }}>
              <h3>{t("Translation.registration")}</h3>
              <div className="row">
                <div className="col-xl-6 col-lg-8 col-md-12">
                  <div className="form-group">
                    {/* First Column */}
                    <Label label={t("Translation.make_of_the_equipment")} />
                    <span className="text-danger fs-1">*</span>
                    <Controller
                      name="equipmentMake"
                      control={control}
                      rules={{ required: t("Translation.msg_equipment_make_is_required") }}
                      render={({ field }) => (
                        <>
                          <Input
                            {...field}
                            inputType="text"
                            className={"form-control"}
                            inputName="equipmentMake"
                            register={register}
                            readOnly={true}
                            rules={{
                              required: {
                                value: true,
                                message: `${t(
                                  "Translation.error_companyname_required"
                                )}`,
                              },
                            }}
                            keyPressHandler={(e: any) => {}}
                            id="equipmentMake"
                            disabled={false}
                          />
                          <FieldError error={errors.equipmentMake?.message} />
                        </>
                      )}
                    />
                  </div>
                  <div className="form-group">
                    <Label label={t("Translation.model")} />
                    <span className="text-danger fs-1">*</span>
                    <Controller
                      name="equipmentModel"
                      control={control}
                      rules={{ required: t("Translation.msg_equipment_model_is_required") }}
                      render={({ field }) => (
                        <>
                          <Input
                            {...field}
                            inputType="text"
                            className={"form-control"}
                            readOnly={true}
                            inputName="equipmentModel"
                            register={register}
                            rules={{
                              required: {
                                value: true,
                                message: `${t(
                                  "Translation.error_recipientname_required"
                                )}`,
                              },
                            }}
                            keyPressHandler={(e: any) => {}}
                            id="equipmentModel"
                            disabled={false}
                          />
                          <FieldError error={errors.equipmentModel?.message} />
                        </>
                      )}
                    />
                  </div>
                  <div className="form-group">
                    <Label label={t("Translation.year_of_manufacture")} />
                    <span className="text-danger fs-1">*</span>

                    <Controller
                      name="yearOfManufactoring"
                      control={control}
                      rules={{
                        required: {
                          value: true,
                          message: t("Translation.msg_year_of_manufacture_is_required"),
                        },
                      }}
                      render={({ field }) => (
                        <MaterialSelect
                          {...field}
                          error={!!errors.yearOfManufactoring}
                          displayEmpty
                          id="equipmentType"
                          sx={{
                            width: "100%",
                            height: "48px",
                            background: "#EFF3F6",
                            color: "#A0AEBD",
                            border: "none",
                            "& .MuiOutlinedInput-notchedOutline": {
                              border: "none",
                            },
                          }}
                        >
                          {(yearOfManufactoringOptions || []).map((item) => (
                            <MenuItem value={item.value} key={item.value}>
                              {item.label}
                            </MenuItem>
                          ))}
                        </MaterialSelect>
                      )}
                    />
                  </div>
                </div>
                <div className="col-xl-6 col-lg-8 col-md-12">
                  <div className="form-group">
                    <Label label={t("Translation.fuel_type")} />
                    <span className="text-danger fs-1">*</span>
                    <Controller
                      name="fuelType"
                      control={control}
                      rules={{
                        required: {
                          value: true,
                          message: t("Translation.fuel_type_required"),
                        },
                      }}
                      render={({ field }) => (
                        <MaterialSelect
                          {...field}
                          error={!!errors.fuelType}
                          displayEmpty
                          disabled={true}
                          id="fuelType"
                          sx={{
                            width: "100%",
                            height: "48px",
                            background: "#EFF3F6",
                            color: "#A0AEBD",
                            border: "none",
                            "& .MuiOutlinedInput-notchedOutline": {
                              border: "none",
                            },
                          }}
                        >
                          {(fuelTypeOptions || []).map((item) => (
                            <MenuItem value={item.value} key={item.value}>
                              {tCheck(item.label)}
                            </MenuItem>
                          ))}
                        </MaterialSelect>
                      )}
                    />
                  </div>
                  <div className="form-group">
                    <Label label={t("Translation.type_of_usage")} />
                    <span className="text-danger fs-1">*</span>
                    <Controller
                      name="typeOfUsage"
                      control={control}
                      rules={{
                        required: {
                          value: true,
                          message: t("Translation.type_of_usage_required"),
                        },
                      }}
                      render={({ field }) => (
                        <>
                          <MaterialSelect
                            {...field}
                            displayEmpty
                            error={!!errors.typeOfUsage}
                            id="typeOfUsage"
                            sx={{
                              width: "100%",
                              height: "48px",
                              background: "#EFF3F6",
                              color: "#A0AEBD",
                              border: "none",
                              "& .MuiOutlinedInput-notchedOutline": {
                                border: "none",
                              },
                            }}
                          >
                            {(typeOfUsageOptions || []).map((item) => (
                              <MenuItem value={item.value} key={item.value}>
                                {item.label}
                              </MenuItem>
                            ))}
                          </MaterialSelect>
                          <FieldError error={errors.typeOfUsage?.message} />
                        </>
                      )}
                    />
                  </div>
                  <div className="form-group">
                    <Label label={t("Translation.if_selected_other")} />
                    <Controller
                      name="additionalNotes"
                      control={control}
                      render={({ field }) => (
                        <Input
                          inputType="text"
                          className={"form-control"}
                          inputName="additionalNotes"
                          register={register}
                          rules={{
                            required: {
                              value: false, // Optional field
                            },
                          }}
                          keyPressHandler={(e: any) => {}}
                          id="additionalNotes"
                          disabled={false}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div style={{ display: "flex" }}>
            {currentStep >= 0 && (
              <button
                type="button"
                className="btn white-btn"
                style={{
                  marginLeft: "30px",
                  display: "flex",
                  paddingLeft: 5,
                }}
                onClick={handleBack}
              >
                <span>
                  <img
                    src={images.BackArrow}
                    alt="back"
                    style={{ margin: "0 10px" }}
                  />
                  {t("Translation.back")}
                </span>
              </button>
            )}

            <button
              type="button"
              className="btn white-btn"
              style={{
                marginLeft: "30px",
                display: "flex",
                paddingRight: 5,
                background: currentStep === 2 ? "#00796b" : "#0B907B",
              }}
              onClick={async (e) => {
                e.preventDefault();
                if (currentStep === 2) {
                  const isValid = await trigger(); // This will trigger validation for all fields
                  if (isValid) {
                    handleSubmit(onSubmit)();
                  } else {
                    toast.error(t("Translation.Err_missing_some_required_fields"));
                  }
                } else {
                  handleNext();
                }
              }}
            >
              <span style={{ color: "#fff" }}>
                {currentStep === 2 ? `${t("Translation.submit")}` : `${t("Translation.next")}`}
                <img
                  src={images.ForwardArrow}
                  alt="next"
                  style={{ margin: "0 10px" }}
                />
              </span>
            </button>
          </div>
        </div>
      </form>
    </>
  );
};

export default RegisterEquipment;
